<template>
	<article class="collection">
		<page-header />
		<form id="collection-update-form" @submit.prevent="modify">
			<managing-section header="기본 정보">
				<managing-box title="제목">
					<text-counter
						ref="title"
						v-model="title"
						:max-length="50"
						placeholder="입력하세요."
						class="collection__text-counter"
					/>
				</managing-box>
				<ManagingBox title="홈타이틀">
					<input
						v-model="shortWording"
						placeholder="입력하세요."
						type="text"
						ref="shortWording"
					/>
				</ManagingBox>
				<managing-box title="노출기간" :horizontal-mode="true">
					<DateRange
						v-model="displayPeriod"
						ref="displayPeriod"
						:disabled="display === 1"
						class="collection__period"
					/>
					<RadioList v-model="display" :radio-list="displayList" />
				</managing-box>
				<managing-box title="태그">
					<text-counter
						ref="tags"
						v-model="tags"
						:max-length="100"
						:use-comma="true"
						placeholder="콤마(,)로 구분"
						class="collection__text-counter"
					/>
				</managing-box>
				<managing-box title="아이콘">
					<icon-adder ref="iconAdder" @change="iconChange" />
				</managing-box>
			</managing-section>
		</form>
		<managing-section
			:header="`등록 제품 리스트(총 ${contentTotal}건)`"
			:is-table="true"
			class="collection__search-section"
		>
			<template slot="infoSection">
				<div class="section__info__left">
					<horizontal-search
						:use-init-button="false"
						@search="searchProduct"
					>
						<select-input
							ref="searchType"
							:default-select-value="searchTypeList[0].value"
							:select-list="searchTypeList"
							:use-realtime-search="true"
							placeholder="입력하세요."
							@enter="searchProduct"
							@search="searchProduct"
						/>
					</horizontal-search>
				</div>
				<div class="section__info__right">
					<div class="section__info__items">
						<selector
							v-model="snapshotDate"
							:option-list="snapshotDateList"
							@input="onInputSnapshotDate"
						/>
					</div>
					<div class="section__info__items">
						<button class="btn-excel" @click="onClickExcelButton">
							엑셀 다운로드
						</button>
					</div>
					<div class="section__info__items">
						<selector
							v-model="limit"
							valueKey="id"
							:option-list="getLimitList()"
						/>
					</div>
				</div>
			</template>
			<product-table :product-list="productList" />
			<pagination-box
				v-if="productList"
				slot="pagination"
				:total-item="contentTotal"
				:offset="offset"
				:limit="limit"
				class="theme__pagination"
				@to="paging"
			/>
		</managing-section>
		<div class="btn-wrap-main">
			<button
				type="button"
				class="btn-main"
				v-text="'미리보기'"
				@click="preview"
			/>
			<button
				form="collection-update-form"
				type="submit"
				class="btn-main blue"
				v-text="'수정'"
			/>
		</div>
	</article>
</template>
<script>
	import compact from 'lodash/compact';
	import moment from 'moment';
	import { mapState, mapGetters } from 'vuex';
	import util from '@/util';
	import PageHeader from '@/components/PageHeader';
	import ManagingSection from '@/components/layout/ManagingSection';
	import ManagingBox from '@/components/layout/ManagingBox';
	import TextCounter from '@/components/TextCounter';
	import DateRange from '@/components/DateRange';
	import RadioList from '@/components/RadioList';
	import IconAdder from '@/components/IconAdder';
	import HorizontalSearch from '@/components/HorizontalSearch';
	import SelectInput from '@/components/SelectInput';
	import Selector from '@/components/Selector';
	import ProductTable from '@/components/ProductTable';
	import PaginationBox from '@/components/PaginationBox';

	export default {
		name: 'CollectionUpdate',
		components: {
			PageHeader,
			ManagingSection,
			ManagingBox,
			TextCounter,
			DateRange,
			RadioList,
			IconAdder,
			HorizontalSearch,
			SelectInput,
			Selector,
			ProductTable,
			PaginationBox,
		},
		data() {
			return {
				idCollection: this.$route.params.idCollection,
				code: '',
				snapshotDateList: [],
				snapshotDate: null,
				originData: null,
				title: '',
				displayPeriod: { from: '', to: '' },
				displayList: [
					{ id: 0, value: '기간설정' },
					{ id: 1, value: '비노출' },
				],
				display: 0,
				tags: '',
				iconPath: '',
				searchTypeList: [
					{ value: 'idProduct', name: '제품번호' },
					{ value: 'productTitle', name: '제품명' },
					{ value: 'brandAndProductTitle', name: '브랜드 + 제품명' },
				],
				limit: 10,
				offset: 0,
				contentTotal: 0,
				productList: null,
				modified: false,
				shortWording: '',
			};
		},
		computed: {
			...mapState({
				IMAGE_STORAGE_PATH: state => state.env.IMAGE_STORAGE_PATH,
			}),
			...mapGetters(['getLimitList']),
		},
		watch: {
			limit() {
				if (this.productList && this.productList.length) {
					this.offset = 0;
					this.setProductList();
				}
			},
		},
		mounted() {
			this.initData();
		},
		async beforeRouteLeave(to, from, next) {
			if (
				!this.modified &&
				this.hasChanges() &&
				confirm(
					'수정된 내용이 존재합니다. 수정 항목을 저장하시겠습니까?'
				)
			) {
				await this.update();
			}
			next();
		},
		methods: {
			async setCollectionData() {
				const { data } = await this.$axiosAdmin2.get(
					`/v3/api/collection/${this.idCollection}`
				);
				this.originData = data;
				this.code = data.code;
				this.$refs.title.textValue = this.title = data.title || '';
				if (data.startOn && data.endOn) {
					const from = moment(data.startOn, 'x').format(
						'YYYY-MM-DDTHH:mm'
					);
					const to = moment(data.endOn, 'x').format(
						'YYYY-MM-DDTHH:mm'
					);
					this.displayPeriod = { from, to };
					this.originData.startOn = from;
					this.originData.endOn = to;
				} else {
					this.display = 1;
				}
				if (data.tags.length) {
					this.$refs.tags.textValue = this.tags = data.tags.join(',');
				}
				if (data.iconPath) {
					this.$refs.iconAdder.src = this.iconPath = data.iconPath;
				}
				this.shortWording = data.shortWording || '';
			},
			async setSnapshotDate() {
				try {
					const {
						data: { snapshotCreatedAtList: list },
					} = await this.$axiosAdmin2.get(
						`/v3/api/collection/${this.code}/snapshot/date/list`
					);
					const firstItem = {
						name: moment(
							this.originData.productUpdatedAt || Date.now()
						).format('YYYY-MM-DD'),
						value: null,
					};
					this.snapshotDateList = list.reduce(
						(acc, date) => {
							acc.push({
								name: moment(date).format('YYYY-MM-DD'),
								value: date,
							});
							return acc;
						},
						[firstItem]
					);
					this.snapshotDate = null;
				} catch (err) {
					console.error(err);
				}
			},
			async getList(isCsv = false) {
				try {
					const params = {
						offset: isCsv ? 0 : this.offset,
						limit: isCsv ? 9999999 : this.limit,
						order: this.order,
					};
					params[this.$refs.searchType.selectModel] =
						this.$refs.searchType.inputModel;
					if (this.snapshotDate) {
						params.snapshotCreatedAt = this.snapshotDate;
					}
					const url = `/v3/api/collection/${this.code}/product/list`;
					const { data } = await this.$axiosAdmin2.get(url, {
						params,
					});
					return { data, url };
				} catch (err) {
					console.error(err);
				}
			},
			async setProductList() {
				const { data } = await this.getList();
				this.contentTotal = data.contentTotal;
				this.productList = data.data.map(({ product, doc = {} }) => ({
					idProduct: product.idProduct,
					productImg: product.imageUrl,
					category: compact([
						product.category.firstCategoryText,
						product.category.secondCategoryText,
						product.category.thirdCategoryText,
					]).join(' > '),
					volume: `${product.volume || '-'} / ${product.price || '-'}`,
					brandTitle: product.brand.brandTitle,
					ratingAvg: product.review.ratingAvg,
					productTitle: product.productTitle,
					reviewCount: product.review.reviewCount,
					doc,
				}));
			},
			async initData() {
				await this.setCollectionData();
				this.setSnapshotDate();
				await this.setProductList();
			},
			onInputSnapshotDate() {
				this.offset = 0;
				this.setProductList();
			},
			iconChange(iconPath) {
				this.iconPath = iconPath;
			},
			searchProduct() {
				this.offset = 0;
				this.setProductList();
			},
			paging({ offset }) {
				this.offset = offset;
				this.setProductList();
			},
			preview() {
				this.$store.commit('modal/toggleModal', {
					id: 'CollectionPreview',
					params: { title: this.title, code: this.code },
				});
			},
			validate() {
				if (!this.title) {
					alert('제목을 입력하세요.');
					this.$refs.title.$refs.input.focus();
					return false;
				} else if (!this.shortWording) {
					alert('홈타이틀을 입력하세요.');
					this.$refs.shortWording.focus();
					return false;
				} else if (!this.display && !this.displayPeriod.from) {
					alert('노출기간 시작시간을 입력하세요.');
					this.$refs.displayPeriod.focus();
					return false;
				} else if (!this.display && !this.displayPeriod.to) {
					alert('노출기간 종료시간을 입력하세요.');
					this.$refs.displayPeriod.focus('to');
					return false;
				} else if (!this.iconPath) {
					alert('아이콘 이미지를 등록허세요.');
					return false;
				}
				return true;
			},
			async getParams() {
				if (!this.validate()) {
					return false;
				}
				const params = {
					title: this.title,
					shortWording: this.shortWording,
				};
				if (!this.display) {
					params.startOn = moment(
						this.displayPeriod.from,
						'YYYY-MM-DDTHH:mm'
					).format('x');
					params.endOn = moment(
						this.displayPeriod.to,
						'YYYY-MM-DDTHH:mm'
					).format('x');
				}
				if (this.tags) {
					params.tags = this.tags.split(',');
				}
				if (this.iconPath.indexOf(this.IMAGE_STORAGE_PATH) < 0) {
					this.iconPath = await this.$refs.iconAdder.upload();
				}
				params.iconPath = this.iconPath.replace(
					this.IMAGE_STORAGE_PATH,
					''
				);
				return params;
			},
			async update() {
				const params = await this.getParams();
				if (params) {
					await this.$axiosAdmin2.put(
						`/v3/api/collection/${this.idCollection}`,
						params
					);
					return true;
				}
				return false;
			},
			async modify() {
				try {
					const updateSuccess = await this.update();
					if (updateSuccess) {
						this.modified = true;
						this.$router.go(-1);
					}
				} catch (err) {
					console.error(err);
				}
			},
			hasChanges() {
				const origin = this.originData;
				return !(
					origin.title === this.title &&
					origin.startOn === this.displayPeriod.from &&
					origin.endOn === this.displayPeriod.to &&
					origin.tags.join(',') === this.tags &&
					origin.iconPath === this.iconPath
				);
			},
			async onClickExcelButton() {
				try {
					const { data, url } = await this.getList(true);
					const rows = data.data.map(({ product, doc = {} }) => [
						product.idProduct,
						product.productTitle,
						product.brand.brandTitle,
						product.category.firstCategoryText,
						product.category.secondCategoryText,
						product.category.thirdCategoryText,
						product.review.reviewCount,
						product.review.ratingAvg,
						product.review.satisfaction,
						product.keyword,
						doc.weeklyWinnerCount,
					]);
					const title = `컬렉션_${this.snapshotDateList.find(({ value }) => value === this.snapshotDate).name}_상품리스트_${Date.now()}`;
					const cols = [
						'제품ID',
						'제품명',
						'브랜드명',
						'1차 카테고리',
						'2차 카테고리',
						'3차 카테고리',
						'리뷰수',
						'리뷰평점',
						'만족도',
						'키워드',
					];
					if ('PC_1007' == this.code) {
						cols.push('연속 1위 주차');
					}
					util.getCsv([rows], undefined, [cols], title, url);
					this.$store.commit('modal/toggleModal', false);
				} catch (err) {
					console.error(err);
				}
			},
		},
	};
</script>
<style lang="scss" scoped>
	.collection__text-counter {
		width: 100%;
	}

	.collection__period {
		margin-right: 16px;
	}

	.collection__search-section ::v-deep {
		.section__table-wrap {
			overflow-y: hidden;
		}

		.product-table {
			margin-bottom: -1px;
		}
	}
</style>
